/**
 * Pay Rules Table Component
 * Displays the list of pay rules with filtering, sorting, and expandable rows
 */
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Search,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MapPin,
  Filter,
  ShieldAlert
} from "lucide-react";

const PayRulesTable = ({
  groupedRules,
  totalRules,
  hasActiveFilters,
  searchTerm,
  setSearchTerm,
  selectedState,
  setSelectedState,
  selectedStatus,
  setSelectedStatus,
  severityFilters,
  setSeverityFilters,
  uniqueStates,
  clearFilters,
  sortConfig,
  handleSort,
  expandedRules,
  toggleExpanded
}) => {
  const navigate = useNavigate();

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Compliant":
        return <Badge className="status-badge status-compliant text-xs px-2 py-0">Compliant</Badge>;
      case "Non-Compliant":
        return <Badge className="status-badge status-non-compliant text-xs px-2 py-0">Non-Compliant</Badge>;
      case "Over-Compliant":
        return <Badge className="status-badge status-over-compliant text-xs px-2 py-0">Over-Compliant</Badge>;
      case "Mixed":
        return <Badge className="bg-purple-500 text-white text-xs px-2 py-0">Mixed</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{status}</Badge>;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "Critical":
        return <Badge className="bg-red-700 text-white text-xs px-2 py-0">Critical</Badge>;
      case "High":
        return <Badge className="bg-orange-500 text-white text-xs px-2 py-0">High</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs px-2 py-0">None</Badge>;
    }
  };

  return (
    <Card className="flex-1 flex flex-col min-h-0" data-testid="pay-rules-table">
      <CardHeader className="py-3 px-4 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-['Manrope']">Pay Rules</CardTitle>
            <span className="text-xs text-muted-foreground">
              ({groupedRules.length} rules{hasActiveFilters ? ` of ${totalRules}` : ''})
            </span>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-7 h-7 w-[140px] text-sm"
                data-testid="search-input"
              />
            </div>

            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-[120px] h-7 text-xs" data-testid="state-filter">
                <MapPin className="w-3 h-3 mr-1 text-muted-foreground" />
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {uniqueStates.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[120px] h-7 text-xs" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Compliant">Compliant</SelectItem>
                <SelectItem value="Non-Compliant">Non-Compliant</SelectItem>
                <SelectItem value="Over-Compliant">Over-Compliant</SelectItem>
              </SelectContent>
            </Select>

            {/* Severity Filter with Checkboxes */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="severity-filter">
                  <ShieldAlert className="w-3 h-3 text-muted-foreground" />
                  Severity
                  {Object.values(severityFilters).some(v => v) && (
                    <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                      {Object.entries(severityFilters).filter(([_, v]) => v).map(([k]) => k[0]).join(',')}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-3" align="start">
                <div className="space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">Filter by Severity</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={severityFilters.Critical}
                        onCheckedChange={(checked) => 
                          setSeverityFilters(prev => ({ ...prev, Critical: checked }))
                        }
                        className="border-red-700 data-[state=checked]:bg-red-700"
                      />
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3 text-red-700" />
                        <span className="text-xs">Critical</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={severityFilters.High}
                        onCheckedChange={(checked) => 
                          setSeverityFilters(prev => ({ ...prev, High: checked }))
                        }
                        className="border-orange-500 data-[state=checked]:bg-orange-500"
                      />
                      <div className="flex items-center gap-1.5">
                        <ShieldAlert className="w-3 h-3 text-orange-500" />
                        <span className="text-xs">High</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={severityFilters.Medium}
                        onCheckedChange={(checked) => 
                          setSeverityFilters(prev => ({ ...prev, Medium: checked }))
                        }
                        className="border-amber-500 data-[state=checked]:bg-amber-500"
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-xs">Medium</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={severityFilters.Low}
                        onCheckedChange={(checked) => 
                          setSeverityFilters(prev => ({ ...prev, Low: checked }))
                        }
                        className="border-sky-500 data-[state=checked]:bg-sky-500"
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-sky-500" />
                        <span className="text-xs">Low</span>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={severityFilters.Info}
                        onCheckedChange={(checked) => 
                          setSeverityFilters(prev => ({ ...prev, Info: checked }))
                        }
                        className="border-slate-500 data-[state=checked]:bg-slate-500"
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-slate-500" />
                        <span className="text-xs">Info</span>
                      </div>
                    </label>
                    <div className="border-t pt-2 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={severityFilters.None}
                          onCheckedChange={(checked) => 
                            setSeverityFilters(prev => ({ ...prev, None: checked }))
                          }
                        />
                        <span className="text-xs text-muted-foreground">No Issues (Compliant)</span>
                      </label>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-xs">
                <Filter className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          <table className="data-table text-sm">
            <thead className="sticky top-0 bg-card z-10">
              <tr>
                <th className="w-8"></th>
                <th className="cursor-pointer hover:bg-muted/50 py-2" onClick={() => handleSort("PayRuleName")}>
                  <div className="flex items-center">Pay Rule {getSortIcon("PayRuleName")}</div>
                </th>
                <th className="cursor-pointer hover:bg-muted/50 py-2" onClick={() => handleSort("CompanyName")}>
                  <div className="flex items-center">Company {getSortIcon("CompanyName")}</div>
                </th>
                <th className="py-2">
                  <div className="flex items-center">States</div>
                </th>
                <th className="cursor-pointer hover:bg-muted/50 py-2" onClick={() => handleSort("EmployeeCount")}>
                  <div className="flex items-center">Emp {getSortIcon("EmployeeCount")}</div>
                </th>
                <th className="cursor-pointer hover:bg-muted/50 py-2" onClick={() => handleSort("ComplianceStatus")}>
                  <div className="flex items-center">Status {getSortIcon("ComplianceStatus")}</div>
                </th>
                <th className="cursor-pointer hover:bg-muted/50 py-2" onClick={() => handleSort("Severity")}>
                  <div className="flex items-center">Severity {getSortIcon("Severity")}</div>
                </th>
                <th className="cursor-pointer hover:bg-muted/50 py-2" onClick={() => handleSort("IssueCount")}>
                  <div className="flex items-center">Issues {getSortIcon("IssueCount")}</div>
                </th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {groupedRules.length > 0 ? (
                groupedRules.map((group) => (
                  <>
                    {/* Parent Row */}
                    <tr
                      key={group.groupKey}
                      className="cursor-pointer hover:bg-muted/50 border-b border-border"
                      data-testid={`pay-rule-group-${group.groupKey}`}
                    >
                      <td className="py-2 pl-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(group.groupKey);
                          }}
                          className="p-1 hover:bg-secondary rounded"
                        >
                          {expandedRules.has(group.groupKey) ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1.5">
                          {group.totalCritical > 0 && (
                            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                          )}
                          <p className="font-medium text-sm truncate max-w-[180px]">{group.PayRuleName}</p>
                        </div>
                      </td>
                      <td className="py-2 text-sm">{group.CompanyName}</td>
                      <td className="py-2">
                        <span className="text-sm text-muted-foreground">{group.stateCount} state{group.stateCount !== 1 ? 's' : ''}</span>
                      </td>
                      <td className="py-2">
                        <span className="text-sm">{group.totalEmployees.toLocaleString()}</span>
                      </td>
                      <td className="py-2">{getStatusBadge(group.overallStatus)}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-1.5">
                          {group.severity === "Critical" && (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-700 flex-shrink-0" />
                          )}
                          {getSeverityBadge(group.severity)}
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          {group.totalCritical > 0 && (
                            <Badge className="bg-red-700 text-white text-[10px] px-1.5 py-0">{group.totalCritical}</Badge>
                          )}
                          {group.totalWarning > 0 && (
                            <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0">{group.totalWarning}</Badge>
                          )}
                          {!group.totalCritical && !group.totalWarning && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/pay-rule/${encodeURIComponent(group.firstPayRuleID)}`);
                          }}
                          className="p-1 hover:bg-secondary rounded"
                        >
                          <ChevronRight className="w-4 h-4 text-primary" />
                        </button>
                      </td>
                    </tr>
                    {/* Expanded State Rows */}
                    {expandedRules.has(group.groupKey) && group.states.map((state, idx) => (
                      <tr
                        key={`${group.groupKey}-${state.State}-${idx}`}
                        className="bg-muted/30 hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/pay-rule/${encodeURIComponent(state.PayRuleID)}`)}
                      >
                        <td className="py-1.5 pl-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <div className="w-px h-4 bg-border" />
                          </div>
                        </td>
                        <td className="py-1.5 pl-4">
                          <span className="text-xs text-muted-foreground">└─</span>
                        </td>
                        <td className="py-1.5"></td>
                        <td className="py-1.5">
                          <div className="flex items-center gap-1.5">
                            {state.CriticalCount > 0 && (
                              <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                            )}
                            <span className="text-sm font-medium">{state.State || "N/A"}</span>
                          </div>
                        </td>
                        <td className="py-1.5">
                          <span className="text-sm">{state.EmployeeCount.toLocaleString()}</span>
                        </td>
                        <td className="py-1.5">{getStatusBadge(state.ComplianceStatus)}</td>
                        <td className="py-1.5">
                          {state.CriticalCount > 0 ? (
                            <div className="flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 text-red-700" />
                              <span className="text-xs text-red-700 font-medium">Critical</span>
                            </div>
                          ) : state.WarningCount > 0 ? (
                            <span className="text-xs text-orange-600 font-medium">High</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-1.5">
                          <div className="flex items-center gap-1">
                            {state.CriticalCount > 0 && (
                              <Badge className="bg-red-700 text-white text-[10px] px-1.5 py-0">{state.CriticalCount}</Badge>
                            )}
                            {state.WarningCount > 0 && (
                              <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0">{state.WarningCount}</Badge>
                            )}
                            {!state.CriticalCount && !state.WarningCount && (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-1.5"></td>
                      </tr>
                    ))}
                  </>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilters ? "No matching pay rules found" : "No pay rules available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayRulesTable;
